// Per-route SEO metadata. Path (with :slug placeholders resolved dynamically at runtime).

export const SITE_ORIGIN = 'https://visotonics.com';

const DEFAULT_TITLE = 'Visotonics — Patented Physical AI for Ports, Terminals & Industrial Operations';
const DEFAULT_DESC =
  "Visotonics' patented Physical AI platform delivers highest-accuracy AI video analytics for ports, container terminals, industrial facilities, smart cities, and critical infrastructure.";

// Static routes (exact match)
export const STATIC_SEO = {
  '/': { title: DEFAULT_TITLE, description: DEFAULT_DESC },
  '/platform': {
    title: 'Platform — Four environments, one vision platform · Visotonics',
    description:
      'One Physical AI stack — deployed on the CCTV you already own. Viso Yard, Viso Warehouse, Viso Factory and Viso Data across every physical operation.',
  },
  '/industries': {
    title: 'Industries — AI vision tuned to four industries · Visotonics',
    description:
      'AI vision tuned to Ports & Terminals, Warehousing & Distribution, Manufacturing, and Logistics & Supply Chain — pre-trained models, integrations and playbooks.',
  },
  '/services': {
    title: 'Services — Eight stages. One outcome · Visotonics',
    description:
      'From assess to transform — advisory, design, build, integration, deployment and 24×7 operate services for Physical AI at industrial scale.',
  },
  '/partners': {
    title: 'Partner Program — Build, sell and deliver with Visotonics',
    description:
      'Register deals, unlock creation rights, and access certified enablement to deliver the Visotonics Physical AI platform.',
  },
  '/contact': {
    title: 'Contact — Book a live demo · Visotonics',
    description:
      'Talk to Visotonics. Book a live demo of AI video analytics for seaports, warehouses, airports and enterprise operations.',
  },
  '/company/about': {
    title: 'About — Builders of AI for the physical world · Visotonics',
    description:
      'Visotonics is founded by a team of computer vision veterans with 6 patents and 10+ core IPs, turning industrial operations into a measurable, verifiable operating record.',
  },
  '/company/careers': {
    title: 'Careers — Come build with us · Visotonics',
    description:
      'Join Visotonics — build the vision layer of the physical world. We hire engineers, product minds, researchers and go-to-market leaders who love atoms as much as bits.',
  },
  '/resources/blog': {
    title: 'Blog — Engineering & product notes · Visotonics',
    description:
      'Field notes on AI vision for logistics — damage detection, gate automation, yard management, warehouse audit and document intelligence.',
  },
  '/resources/faqs': {
    title: 'FAQs — Answers to the questions we hear most · Visotonics',
    description:
      'Everything about the Visotonics Physical AI platform — deployment, integrations, security, and pricing.',
  },
  '/legal/privacy': { title: 'Privacy Policy · Visotonics', description: 'Visotonics privacy policy: what data we collect, how we use it, and your rights.' },
  '/legal/terms': { title: 'Terms of Service · Visotonics', description: 'Visotonics terms of service for accessing and using the platform.' },
  '/legal/security': { title: 'Security · Visotonics', description: 'How Visotonics protects your data — governance, access control, encryption and incident response.' },
  '/login': { title: 'Log in · Visotonics', description: 'Sign in to your Visotonics account.' },
  '/register': { title: 'Create account · Visotonics', description: 'Get started with Visotonics.' },
  '/partner/register': { title: 'Partner Registration · Visotonics', description: 'Register as a Visotonics partner.' },
};

// Dynamic slug → title lookups
export const PLATFORM_SLUG_TITLE = {
  'viso-yard': ['Viso Yard — Container & Terminal Vision · Visotonics', 'Container Vision, Gate Vision, Yard Vision and more — nine terminal-grade AI vision products deployed on existing CCTV.'],
  'viso-warehouse': ['Viso Warehouse — Warehouse & DC Vision · Visotonics', 'Cargo Vision, Audit Vision, Dimension Vision and more — from inbound to outbound, running on the cameras already covering your floor.'],
  'viso-factory': ['Viso Factory — Production & Line Vision · Visotonics', 'Production Vision, Audit Vision, Dimension Vision — count, SKU and inspect on the same camera as the unit passes.'],
  'viso-data': ['Viso Data — Foundation AI · Visotonics', 'Compression AI, Trace AI, Detect AI — the footage layer beneath every checkpoint.'],
};

export const INDUSTRY_SLUG_TITLE = {
  'ports-terminals': ['Ports & Terminals — AI-Powered Terminal Intelligence · Visotonics', 'Gate, crane and yard vision for terminals. Every container, every checkpoint, verified — the moment it crosses the gate.'],
  'warehousing-distribution': ['Warehousing & Distribution — AI-Powered Warehouse Intelligence · Visotonics', 'Every case counted, every pallet measured — from inbound to outbound, on the cameras already covering your floor.'],
  'manufacturing': ['Manufacturing — AI-Powered Production Intelligence · Visotonics', 'Every item counted, classified and inspected the moment it leaves the line.'],
  'logistics-supply-chain': ['Logistics & Supply Chain — AI-Powered Chain Intelligence · Visotonics', 'One record, every handoff — from vessel to gate to dock to line.'],
};

export const BLOG_SLUG_TITLE = {
  'container-damage-detection': ['Container Damage Detection with AI · Visotonics Blog', 'Improving visibility with AI — automatic six-face damage inspection from your existing CCTV.'],
  'warehouse-loading-unloading': ['Warehouse Loading & Unloading with AI · Visotonics Blog', 'Portable trolley-mounted AI cameras eliminate blind spots at the dock.'],
  'container-gate-automation': ['Container Gate Automation with AI · Visotonics Blog', 'Reading container IDs, ISO codes and trailer IDs automatically at the gate.'],
  'container-yard-management': ['Container Yard Management with AI · Visotonics Blog', 'A real-time digital twin of container locations, cutting crane travel time.'],
  'document-processing': ['AI-Powered Document Processing · Visotonics Blog', 'Bills of lading, invoices and shipping documents read into structured data.'],
};

export function seoForPath(pathname) {
  const clean = pathname.replace(/\/+$/, '') || '/';
  if (STATIC_SEO[clean]) return { ...STATIC_SEO[clean], path: clean };

  // dynamic slug matches
  let m = clean.match(/^\/platform\/([^/]+)$/);
  if (m && PLATFORM_SLUG_TITLE[m[1]]) {
    const [title, description] = PLATFORM_SLUG_TITLE[m[1]];
    return { title, description, path: clean };
  }
  m = clean.match(/^\/industries\/([^/]+)$/);
  if (m && INDUSTRY_SLUG_TITLE[m[1]]) {
    const [title, description] = INDUSTRY_SLUG_TITLE[m[1]];
    return { title, description, path: clean };
  }
  m = clean.match(/^\/resources\/blog\/([^/]+)$/);
  if (m && BLOG_SLUG_TITLE[m[1]]) {
    const [title, description] = BLOG_SLUG_TITLE[m[1]];
    return { title, description, path: clean };
  }

  return { title: DEFAULT_TITLE, description: DEFAULT_DESC, path: clean };
}
